<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractTermEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
    term: {
      type: Object,
      required: false,
    },
    mode: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getConfiguration = (...args) => {
    switch (props.mode) {
      case 'add':
        return {
          isEditable: true,
          isDeletable: false,
          isTermReadonly: false,
        };

      case 'edit':
        return {
          isEditable: !props.term.locked,
          isDeletable: !props.term.locked,
          isTermReadonly: props.term.locked,
        };
    }
  };

  const formId = ref(null);
  const labelAlign = ref('right');
  const isEditable = ref(null);
  const isDeletable = ref(null);
  const isTermReadonly = ref(null);
  const messages = ref({
    add: {
      title: $fn.value.pgettext('Contract|ModalHeader', 'Add term'),
    },
    edit: {
      title: $fn.value.pgettext('Contract|ModalHeader', 'Edit term'),
    },
  });
  const item = ref(null);
  const currencies = ref(null);
  const services = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'md',
  }));
  const b_5731695935601903_dataSource = computed(() => ({
    name: 'suggest_product',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    labelAlign: labelAlign.value,
    isEditable: isEditable.value,
    isDeletable: isDeletable.value,
    isTermReadonly: isTermReadonly.value,
    messages: messages.value,
    item: item.value,
    currencies: currencies.value,
    services: services.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_5731695935601903_dataSource: b_5731695935601903_dataSource.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_isEditable = () => {
    return getConfiguration().isEditable;
  };
  const $$load_isDeletable = () => {
    return getConfiguration().isDeletable;
  };
  const $$load_isTermReadonly = () => {
    return getConfiguration().isTermReadonly;
  };
  const $$load_item = () => {
    return (
      props.term ||
      $fn.value.schemaDefaults('CreateContractTerm', {
        contract_id: props.contract.id,
      })
    );
  };
  const $$load_currencies = () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_services = () => {
    return $fn.value
      .fetch('suggest_product', {
        parameters: { query: '*', top: 50, skip: 0 },
      })
      .then((data) => _.orderBy(data, [(p) => p.name.toLowerCase()]));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'isEditable' },
      { name: 'isDeletable' },
      { name: 'isTermReadonly' },
      { name: 'messages' },
      { name: 'item' },
      { name: 'currencies' },
      { name: 'services' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_5731695935601903_dataSource', nodeId: '5731695935601903' },
    ],
    loaders: {
      formId: $$load_formId,
      isEditable: $$load_isEditable,
      isDeletable: $$load_isDeletable,
      isTermReadonly: $$load_isTermReadonly,
      item: $$load_item,
      currencies: $$load_currencies,
      services: $$load_services,
    },
    ux: {},
    dataRefs: {
      formId,
      labelAlign,
      isEditable,
      isDeletable,
      isTermReadonly,
      messages,
      item,
      currencies,
      services,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_5731695935601903_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const currentObjectType = computed(() => {
    return 'contract_term';
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return { title: messages.value[props.mode].title };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.term,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('term');
    }
  );
  watch(
    () => props.mode,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('mode');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_8f3wp0zit_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_r9ekwh41d_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_qix61eg1n_getActionArgs = async ($event) => {
    return {
      objectType: currentObjectType.value,
      op: 'upsert',
      data: item.value,
      options: {
        parameters: {
          rev: props.contract.revision_number,
        },
      },
    };
  };
  const act_5731695935601903_setData_mv8avneu9_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.name',
      value: $event.data.option.name,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5731695935601903_setData_kntjkp3nk_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.service_id',
      value: $event.data.option.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5731695935601903_setData_jwa781ue0_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.unit',
      value: $event.data.option.unit,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2140198529698299_closeModal_15rd67hsn_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2140198529698299_request_j0zu7b6gh_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_contract_term',
      data: item.value,
    };
  };

  const act_7315092382398562_closeModal_8f3wp0zit =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_8f3wp0zit_getActionArgs,
    });
  const inst_evh_7315092382398562_close_9w6fnm17n =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_8f3wp0zit,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_9w6fnm17n],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_z1peqpz7i =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_ldozv2z6h =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_z1peqpz7i,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_ldozv2z6h],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_zdlcb9aab =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_c894z3qtf =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_zdlcb9aab,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_c894z3qtf],
    event: 'close',
  });
  const act_1040167445267876_closeModal_r9ekwh41d =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_r9ekwh41d_getActionArgs,
    });
  const inst_evh_1040167445267876_success_pz8sq8jhx =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_r9ekwh41d,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_qix61eg1n = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_qix61eg1n_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_pz8sq8jhx],
  });
  const inst_evh_1040167445267876_submit_9fdl9i6gl =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_qix61eg1n,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_9fdl9i6gl],
    event: 'submit',
  });
  const act_5731695935601903_setData_mv8avneu9 = new actionTypes.SetDataAction({
    actionArgs: act_5731695935601903_setData_mv8avneu9_getActionArgs,
  });
  const inst_evh_5731695935601903_optionSelected_tiw6ldtvb =
    new actionTypes.EventHandlerImpl({
      action: act_5731695935601903_setData_mv8avneu9,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_5731695935601903_setData_kntjkp3nk = new actionTypes.SetDataAction({
    actionArgs: act_5731695935601903_setData_kntjkp3nk_getActionArgs,
  });
  const inst_evh_5731695935601903_optionSelected_qqw7g8bod =
    new actionTypes.EventHandlerImpl({
      action: act_5731695935601903_setData_kntjkp3nk,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_5731695935601903_setData_jwa781ue0 = new actionTypes.SetDataAction({
    actionArgs: act_5731695935601903_setData_jwa781ue0_getActionArgs,
  });
  const inst_evh_5731695935601903_optionSelected_2d776p91u =
    new actionTypes.EventHandlerImpl({
      action: act_5731695935601903_setData_jwa781ue0,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_5731695935601903_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_5731695935601903_optionSelected_tiw6ldtvb,
        inst_evh_5731695935601903_optionSelected_qqw7g8bod,
        inst_evh_5731695935601903_optionSelected_2d776p91u,
      ],
      event: 'optionSelected',
    });
  const act_2140198529698299_closeModal_15rd67hsn =
    new actionTypes.CloseModalAction({
      actionArgs: act_2140198529698299_closeModal_15rd67hsn_getActionArgs,
    });
  const inst_evh_2140198529698299_success_5u5cs7m0z =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeModal_15rd67hsn,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_2140198529698299_request_j0zu7b6gh = new actionTypes.RequestAction({
    actionArgs: act_2140198529698299_request_j0zu7b6gh_getActionArgs,
    displayName: 'deleteTerm',
    events: [inst_evh_2140198529698299_success_5u5cs7m0z],
  });
  const inst_evh_2140198529698299_clickPreventStop_o15xa1cka =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_request_j0zu7b6gh,
      event: 'click.prevent.stop',
      displayName: 'deleteTerm',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_o15xa1cka],
      event: 'click.prevent.stop',
    });
  const act_2167826738365259_closeComponent_59n6u8gs8 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2167826738365259_clickPreventStop_0qgdp5cqj =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeComponent_59n6u8gs8,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_0qgdp5cqj],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_9w6fnm17n,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_ldozv2z6h,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_c894z3qtf,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_pz8sq8jhx,
    inst_evh_1040167445267876_submit_9fdl9i6gl,
    save: inst_evh_1040167445267876_submit_9fdl9i6gl,
    inst_evh_1040167445267876_submit,
    inst_evh_5731695935601903_optionSelected_tiw6ldtvb,
    inst_evh_5731695935601903_optionSelected_qqw7g8bod,
    inst_evh_5731695935601903_optionSelected_2d776p91u,
    inst_evh_5731695935601903_optionSelected,
    inst_evh_2140198529698299_success_5u5cs7m0z,
    inst_evh_2140198529698299_clickPreventStop_o15xa1cka,
    deleteTerm: inst_evh_2140198529698299_clickPreventStop_o15xa1cka,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2167826738365259_clickPreventStop_0qgdp5cqj,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_9w6fnm17n = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_9w6fnm17n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_ldozv2z6h = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_ldozv2z6h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_c894z3qtf = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_c894z3qtf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_pz8sq8jhx = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_pz8sq8jhx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_9fdl9i6gl = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_9fdl9i6gl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected_tiw6ldtvb = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5731695935601903_optionSelected_tiw6ldtvb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected_qqw7g8bod = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5731695935601903_optionSelected_qqw7g8bod.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected_2d776p91u = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5731695935601903_optionSelected_2d776p91u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5731695935601903_optionSelected = async (event, scope) => {
    return await actions.inst_evh_5731695935601903_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_5u5cs7m0z = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_5u5cs7m0z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_o15xa1cka = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_o15xa1cka.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_0qgdp5cqj = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_0qgdp5cqj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractTermEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="contract"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <ItpFormGroup
            name="service"
            :label="pgettext('ContractTerm', 'Service')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
            v-if="!isTermReadonly"
          >
            <ItpFormSearchableSelect
              name="client"
              v-model="item.service"
              bind-object
              :dataSource="b_5731695935601903_dataSource"
              textField="name"
              @optionSelected="evh_5731695935601903_optionSelected($event, {})"
            >
            </ItpFormSearchableSelect>
          </ItpFormGroup>
          <ItpFormGroup
            name="name"
            :label="pgettext('ContractTerm', 'Name')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpFormInput
              name="name"
              v-model="item.name"
              required
              :readonly="isTermReadonly"
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            name="valid_from"
            :label="pgettext('ContractTerm', 'Valid from')"
            :label-cols-md="4"
            :content-cols-md="4"
            :labelAlign="labelAlign"
          >
            <ItpFormDatetime
              name="valid_from"
              v-model="item.valid_from"
              :readonly="isTermReadonly"
            >
            </ItpFormDatetime>
          </ItpFormGroup>
          <ItpFormGroup
            name="valid_to"
            :label="pgettext('ContractTerm', 'Valid to')"
            :label-cols-md="4"
            :content-cols-md="4"
            :labelAlign="labelAlign"
          >
            <ItpFormDatetime
              name="valid_to"
              v-model="item.valid_to"
              :readonly="isTermReadonly"
            >
            </ItpFormDatetime>
          </ItpFormGroup>
          <hr />
          <ItpFormGroup
            :label="pgettext('ContractTerm', 'Amount')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
            contentClass="input-group"
          >
            <ItpFormInput
              type="number"
              name="amount"
              v-model="item.amount"
              class="col-8"
            >
            </ItpFormInput>
            <ItpFormNativeSelect
              name="currency"
              v-model="item.currency"
              :options="currencies"
              textField="code"
              valueField="code"
              class="col-4"
            >
            </ItpFormNativeSelect>
          </ItpFormGroup>
          <ItpFormGroup
            name="quantity"
            :label="pgettext('ContractTerm', 'Quantity')"
            :label-cols-md="4"
            :content-cols-md="4"
            :labelAlign="labelAlign"
            contentClass="input-group"
          >
            <ItpFormInput type="number" name="quantity" v-model="item.quantity">
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            name="unit"
            :label="pgettext('ContractTerm', 'Unit')"
            :label-cols-md="4"
            :content-cols-md="4"
            :labelAlign="labelAlign"
            contentClass="input-group"
          >
            <ItpFormInput name="unit" v-model="item.unit"> </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            name="notes"
            :label="pgettext('ContractTerm', 'Notes')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpFormTextarea name="notes" v-model="item.notes">
            </ItpFormTextarea>
          </ItpFormGroup>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Delete Term')"
          variant="danger"
          icon="fa-times"
          :spinning="actions.delete && actions.delete.isRunning"
          class="me-auto"
          v-if="mode === 'edit' && isDeletable"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.contract || !forms.contract.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save && actions.save.isRunning"
          v-if="isEditable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
